import { applicationDetailsSchema } from 'api/services/applicants/applicants-service.schema';
import { claimDetailsSchema } from 'api/services/shift-claim/shift-claim.schema';
import {
  skillTypeSchema,
  vatSchema,
  workerEmploymentTypeSchema,
} from 'api/services/zod-schemas/schema';
import { z } from 'zod';

const freelanceShiftPropertiesSchema = z.object({
  hourlyRateCents: z.number(),
  mutableUntilMinutes: z.number(),
  ortApplicable: z.boolean(),
});

const tempWorkShiftPropertiesSchema = z.object({
  grossHourlyRateCents: z.number(),
  cla: z.string(),
  ortApplicable: z.boolean(),
});

const flexPoolSchema = z.object({
  id: z.string(),
  name: z.string(),
  directClaimAllowed: z.boolean(),
});

const visibilityRulesSchema = z.array(
  z.object({
    durationConditionInHours: z.number(),
    isoRuleStartTime: z.string(),
    isoRuleEndTime: z.string(),
    workers: z.array(
      z.object({
        id: z.string(),
        firstName: z.string(),
        lastName: z.string(),
        infix: z.string().nullable(),
        partnerName: z.string(),
        status: z.string(),
        employmentType: workerEmploymentTypeSchema,
      })
    ),
  })
);

const locationSchema = z.object({
  id: z.string(),
  address: z.string(),
  city: z.string().nullable(),
  country: z.string().nullable(),
  houseNumber: z.string().nullable(),
  postalCode: z.string().nullable(),
  street: z.string().nullable(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
});

const HistoryEventSchema = z.any();

export const shiftDetailSchema = z.object({
  id: z.string(),
  name: z.string(),
  companyId: z.string(),
  companyName: z.string(),
  branchOfficeName: z.string(),
  departmentName: z.string(),
  departmentId: z.string(),
  jobTypeId: z.string().nullable(),

  isoEndDateTime: z.string(),
  isoStartDateTime: z.string(),
  startTime: z.string(),
  endTime: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  mutableUntilDate: z.string(),

  workCooldownMinutes: z.number(),
  breakMinutes: z.number(),

  maxClaimants: z.number(),
  flexPools: z.array(flexPoolSchema),
  flexPoolOnly: z.boolean(),
  vat: vatSchema,
  location: locationSchema,
  contactName: z.string(),
  contactPhoneNumber: z.string(),
  history: z.array(HistoryEventSchema),

  claimants: z.array(claimDetailsSchema),
  applicants: z.array(applicationDetailsSchema),
  isFreelance: z.boolean(),
  isTempwork: z.boolean(),
  isEmployee: z.boolean(),

  freelanceProperties: freelanceShiftPropertiesSchema.optional(),
  tempWorkProperties: tempWorkShiftPropertiesSchema.optional(),

  imageUrl: z.string().nullable(),
  descriptionShort: z.string(),
  descriptionLong: z.string(),
  descriptionLawRequirements: z.string(),
  shiftCertificates: z.array(z.object({ name: z.string(), id: z.string() })),
  jobTypeCertificates: z.array(z.object({ name: z.string(), id: z.string() })),
  requirements: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      type: skillTypeSchema,
    })
  ),
  companyGrossHourlyRate: z.number().optional(),
  costCenter: z.string().nullable(),
  isCompanyOpus: z.boolean(),

  visibilityRules: visibilityRulesSchema.optional(),
});

export type ShiftDetail = z.infer<typeof shiftDetailSchema>;

export const shiftPopoverDetailSchema = z.object({
  id: z.string(),
  name: z.string(),
  maxClaimants: z.number(),
  flexPools: z.array(flexPoolSchema),
  claimants: z.array(
    z.object({
      firstName: z.string(),
      lastName: z.string(),
      hasFlexPools: z.boolean(),
      employmentType: z.string(),
      imageUrl: z.string().nullable(),
      label: z.object({
        workerType: workerEmploymentTypeSchema.optional(),
        partnerName: z.string().optional(),
      }),
    })
  ),
  partners: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
    })
  ),
});
export type ShiftPopoverDetail = z.infer<typeof shiftPopoverDetailSchema>;
